/* eslint-disable react-hooks/immutability */
'use client';

import { useCallback, useEffect, useRef } from 'react';

interface AnimationLoopDeps {
  onFrame: (time: number, timePassed: number) => void;
}

/**
 * Animation loop management hook
 * Manages game loop using requestAnimationFrame
 */
export function useAnimationLoop({ onFrame }: AnimationLoopDeps) {
  const animationIdRef = useRef<number | undefined>(undefined);
  const lastTimeRef = useRef<number>(0);
  const isRunningRef = useRef<boolean>(false);

  const animate = useCallback(
    (time: number) => {
      if (!isRunningRef.current) return;

      if (lastTimeRef.current) {
        const timePassed = time - lastTimeRef.current;
        onFrame(time, timePassed);
      }

      lastTimeRef.current = time;
      animationIdRef.current = requestAnimationFrame(animate);
    },
    [onFrame],
  );

  const startAnimation = useCallback(() => {
    if (isRunningRef.current) return;

    isRunningRef.current = true;
    lastTimeRef.current = 0;
    animationIdRef.current = requestAnimationFrame(animate);
  }, [animate]);

  const stopAnimation = useCallback(() => {
    isRunningRef.current = false;

    if (animationIdRef.current) {
      cancelAnimationFrame(animationIdRef.current);
      animationIdRef.current = undefined;
    }

    lastTimeRef.current = 0;
  }, []);

  const restartAnimation = useCallback(() => {
    stopAnimation();
    startAnimation();
  }, [stopAnimation, startAnimation]);

  useEffect(() => {
    return () => {
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
    };
  }, []);

  return {
    animationIdRef,
    isRunning: isRunningRef.current,
    startAnimation,
    stopAnimation,
    restartAnimation,
  };
}
